import type { SpotlightShape } from './types';

export type { SpotlightShape };

function parseBorderRadiusPx(
  value: string,
  width: number,
  height: number,
): number {
  if (!value || value === '0px') {
    return 0;
  }

  let maxRadius = 0;
  for (const part of value.split(/\s+/)) {
    if (part.endsWith('%')) {
      const percent = parseFloat(part);
      if (!Number.isNaN(percent)) {
        maxRadius = Math.max(
          maxRadius,
          (Math.min(width, height) * percent) / 100,
        );
      }
      continue;
    }

    const pixels = parseFloat(part);
    if (Number.isNaN(pixels)) {
      continue;
    }

    if (pixels >= 999) {
      maxRadius = Math.max(maxRadius, Math.min(width, height) / 2);
      continue;
    }

    maxRadius = Math.max(maxRadius, pixels);
  }

  return maxRadius;
}

function getElementBorderRadius(
  element: HTMLElement,
  width: number,
  height: number,
): number {
  const style = window.getComputedStyle(element);
  const corners = [
    style.borderTopLeftRadius,
    style.borderTopRightRadius,
    style.borderBottomRightRadius,
    style.borderBottomLeftRadius,
  ];

  return corners.reduce(
    (maxRadius, corner) =>
      Math.max(maxRadius, parseBorderRadiusPx(corner, width, height)),
    0,
  );
}

function resolveSpotlightBorderRadius(
  element: HTMLElement,
  width: number,
  height: number,
): number {
  let current: HTMLElement | null = element;
  while (current) {
    const radius = getElementBorderRadius(current, width, height);
    if (radius > 0) {
      return radius;
    }
    current = current.parentElement;
  }
  return 0;
}

export function getSpotlightShape(
  element: HTMLElement,
  padding: number,
  inset = 0,
): SpotlightShape {
  const rect = element.getBoundingClientRect();
  const left = Math.floor(rect.left - padding) + inset;
  const top = Math.floor(rect.top - padding) + inset;
  const right = Math.ceil(rect.right + padding) - inset;
  const bottom = Math.ceil(rect.bottom + padding) - inset;
  const width = Math.max(0, right - left);
  const height = Math.max(0, bottom - top);
  const elementRadius = resolveSpotlightBorderRadius(
    element,
    rect.width,
    rect.height,
  );

  return {
    top,
    left,
    width,
    height,
    borderRadius: Math.min(
      Math.max(0, elementRadius + padding - inset),
      width / 2,
      height / 2,
    ),
  };
}

export function areSpotlightShapesEqual(
  a: SpotlightShape,
  b: SpotlightShape,
): boolean {
  return (
    a.top === b.top &&
    a.left === b.left &&
    a.width === b.width &&
    a.height === b.height &&
    a.borderRadius === b.borderRadius
  );
}

export function buildSpotlightMaskImage(
  viewportWidth: number,
  viewportHeight: number,
  shape: SpotlightShape,
): string {
  const { top, left, width, height, borderRadius } = shape;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${viewportWidth}" height="${viewportHeight}"><rect width="100%" height="100%" fill="white"/><rect x="${left}" y="${top}" width="${width}" height="${height}" rx="${borderRadius}" ry="${borderRadius}" fill="black"/></svg>`;

  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}
